import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTutorial } from '../contexts/TutorialContext';
import { useDraggablePanelPosition } from '../hooks/useDraggablePanelPosition';
import type { TutorialStep } from '../tutorial/steps';

/** Above Leaflet controls, NewSite layer-hint (was z-10000), and any route z-[1000] chrome. */
const TUTORIAL_OVERLAY_Z = 100050;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function getHighlightRect(selector: string, pad = 8): Rect | null {
  if (!selector) return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return {
    top: r.top - pad,
    left: r.left - pad,
    width: r.width + pad * 2,
    height: r.height + pad * 2,
  };
}

function collectHighlightRects(step: TutorialStep): Rect[] {
  const selectors = step.highlightSelectors?.length
    ? step.highlightSelectors
    : step.highlightSelector
      ? [step.highlightSelector]
      : [];
  const out: Rect[] = [];
  for (const sel of selectors) {
    const r = getHighlightRect(sel, sel.includes('customer-type-sidebar') ? 12 : 8);
    if (r) out.push(r);
  }
  return out;
}

function unionRect(rects: Rect[]): Rect | null {
  if (!rects.length) return null;
  let top = rects[0].top;
  let left = rects[0].left;
  let right = rects[0].left + rects[0].width;
  let bottom = rects[0].top + rects[0].height;
  for (let i = 1; i < rects.length; i++) {
    const r = rects[i];
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    right = Math.max(right, r.left + r.width);
    bottom = Math.max(bottom, r.top + r.height);
  }
  return { top, left, width: right - left, height: bottom - top };
}

export default function TutorialOverlay() {
  const { t } = useTranslation();
  const { active, currentStep, stepConfig, totalSteps, gateOpen, advance, exit, sandboxCreateError } = useTutorial();
  const [highlightRects, setHighlightRects] = useState<Rect[]>([]);
  const [confirmExit, setConfirmExit] = useState(false);
  const rafRef = useRef<number>(0);
  const popupRef = useRef<HTMLDivElement>(null);

  const { dragPos, isDragging, draggingRef, handleDragHandlePointerDown } = useDraggablePanelPosition(
    popupRef,
    currentStep
  );

  const rect = useMemo(() => unionRect(highlightRects), [highlightRects]);

  /** Prefer anchoring the card to one element (e.g. upload) so it does not cover controls. */
  const popupPositionRect = useMemo(() => {
    const anchor = stepConfig.popupAnchorSelector;
    if (anchor) {
      const a = getHighlightRect(anchor);
      if (a) return a;
    }
    return rect;
  }, [stepConfig.popupAnchorSelector, highlightRects, rect]);

  // draggingRef is read inside update(); ref identity is stable — do not add to deps.
  useEffect(() => {
    if (!active) return;
    let mounted = true;
    const update = () => {
      if (!mounted) return;
      if (!draggingRef.current) {
        setHighlightRects(collectHighlightRects(stepConfig));
      }
      rafRef.current = requestAnimationFrame(update);
    };
    rafRef.current = requestAnimationFrame(update);
    return () => { mounted = false; cancelAnimationFrame(rafRef.current); };
  }, [active, stepConfig]);

  // When the step changes, scroll the primary highlight target into view if it
  // is off-screen. The ring is drawn at the element's viewport rect, so an
  // off-screen target reads as a "broken"/misplaced highlight. The continuous
  // rAF loop above already tracks scroll/resize; this just ensures the target
  // is actually visible to begin with (RCA 2026-06-15). Deferred so the new
  // route/controls have mounted before we measure.
  useEffect(() => {
    if (!active) return;
    const selectors = stepConfig.highlightSelectors?.length
      ? stepConfig.highlightSelectors
      : stepConfig.highlightSelector
        ? [stepConfig.highlightSelector]
        : [];
    if (!selectors.length) return;
    const id = window.setTimeout(() => {
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const offscreen =
          r.top < 0 || r.bottom > window.innerHeight || r.left < 0 || r.right > window.innerWidth;
        if (offscreen) {
          el.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
        }
        break; // first present selector is the primary anchor
      }
    }, 350);
    return () => window.clearTimeout(id);
  }, [active, stepConfig]);

  if (!active) return null;

  const hasHighlightRegions = Boolean(
    stepConfig.highlightSelectors?.length || stepConfig.highlightSelector,
  );
  const isCenter =
    !hasHighlightRegions &&
    (stepConfig.position === 'center' || stepConfig.position === undefined);
  const showNext = stepConfig.gateType === 'manual' || gateOpen;

  let autoPopupStyle: React.CSSProperties = {};
  const POPUP_MARGIN = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  /** Approximate card height for vertical clamping (header + body + footer) */
  const POPUP_EST_HEIGHT = 440;
  const clampLeft = (l: number, mw: number) => Math.min(Math.max(POPUP_MARGIN, l), vw - mw - POPUP_MARGIN);
  const clampTop = (t: number) => Math.min(Math.max(POPUP_MARGIN, t), vh - POPUP_EST_HEIGHT - POPUP_MARGIN);

  if (isCenter) {
    autoPopupStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  } else if (popupPositionRect) {
    const pos = stepConfig.position || 'bottom';
    const pr = popupPositionRect;
    if (pos === 'bottom') {
      const desiredTop = pr.top + pr.height + 16;
      autoPopupStyle = { top: clampTop(desiredTop), left: clampLeft(pr.left, 400), maxWidth: 400 };
    } else if (pos === 'top') {
      autoPopupStyle = {
        bottom: window.innerHeight - pr.top + 16,
        left: clampLeft(pr.left, 400),
        maxWidth: 400,
      };
    } else if (pos === 'right') {
      const popupWidth = 360;
      const gap = 16;
      const rightLeft = pr.left + pr.width + gap;
      if (rightLeft + popupWidth <= vw - POPUP_MARGIN) {
        autoPopupStyle = { top: clampTop(pr.top), left: clampLeft(rightLeft, popupWidth), maxWidth: popupWidth };
      } else {
        const leftLeft = pr.left - gap - popupWidth;
        if (leftLeft >= POPUP_MARGIN) {
          autoPopupStyle = { top: clampTop(pr.top), left: clampLeft(leftLeft, popupWidth), maxWidth: popupWidth };
        } else {
          const desiredTop = pr.top + pr.height + gap;
          autoPopupStyle = { top: clampTop(desiredTop), left: clampLeft(pr.left, 400), maxWidth: 400 };
        }
      }
    } else {
      // 'left': prefer panel left of highlight; if that sits under the sidebar / off-screen, flip to the right
      const popupWidth = 360;
      const gap = 16;
      let idealLeft = pr.left - gap - popupWidth;
      if (idealLeft < POPUP_MARGIN) {
        idealLeft = pr.left + pr.width + gap;
      }
      autoPopupStyle = {
        top: clampTop(pr.top),
        left: clampLeft(idealLeft, popupWidth),
        maxWidth: popupWidth,
      };
    }
  } else {
    autoPopupStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const popupStyle: React.CSSProperties = dragPos
    ? {
        top: dragPos.y,
        left: dragPos.x,
        maxWidth: autoPopupStyle.maxWidth || 400,
        transform: 'none',
      }
    : autoPopupStyle;

  const overlay = (
    <div
      className="fixed inset-0"
      style={{ pointerEvents: 'none', zIndex: TUTORIAL_OVERLAY_Z }}
    >
      {/* Progress bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gray-800" style={{ pointerEvents: 'auto' }}>
        <div
          className="h-full bg-blue-500 transition-all duration-500"
          style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
        />
      </div>

      {/* Highlight rings (no full-screen dim — keeps the rest of the UI fully usable) */}
      {!isCenter && highlightRects.map((hr, i) => (
        <div
          key={i}
          className="absolute border-2 border-blue-400 rounded-lg shadow-[0_0_0_4px_rgba(59,130,246,0.35)] transition-all duration-300"
          style={{
            top: hr.top,
            left: hr.left,
            width: hr.width,
            height: hr.height,
            pointerEvents: 'none',
          }}
        />
      ))}

      {/* Popup card — drag handle: blue header (see useDraggablePanelPosition RCA) */}
      <div
        ref={popupRef}
        data-tutorial="instruction-panel"
        className="absolute bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
        style={{ ...popupStyle, pointerEvents: 'auto', minWidth: 320 }}
      >
        <div
          role="toolbar"
          aria-label="Move tutorial panel"
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-5 py-3 select-none touch-none"
          style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
          onPointerDown={handleDragHandlePointerDown}
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono opacity-75">
              {t('tutorial.overlay.stepOf', { current: currentStep + 1, total: totalSteps })}
            </span>
            <button
              type="button"
              onClick={() => setConfirmExit(true)}
              onPointerDown={(e) => e.stopPropagation()}
              className="text-white/60 hover:text-white text-xs"
            >
              {t('tutorial.overlay.exit')}
            </button>
          </div>
          <h3 className="text-lg font-bold mt-1">{stepConfig.title}</h3>
        </div>

        <div className="px-5 py-4">
          <p className="text-sm text-gray-700 leading-relaxed">{stepConfig.body}</p>

          {stepConfig.gateKey === 'projectSaved' && sandboxCreateError && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {t('tutorial.overlay.sandboxError')}
            </div>
          )}

          {stepConfig.tip && (
            <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-800">
              {t('tutorial.overlay.tipPrefix')} {stepConfig.tip}
            </div>
          )}

          {stepConfig.gateType === 'auto' && !gateOpen && (
            <div className="mt-3 flex items-center gap-2 text-xs text-amber-700">
              <div className="animate-pulse w-2 h-2 rounded-full bg-amber-500" />
              {t('tutorial.overlay.waitingGate')}
            </div>
          )}
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t flex items-center justify-between">
          <span className="text-xs text-gray-400">
            {currentStep > 0 && t('tutorial.overlay.inProgress')}
          </span>
          <button
            type="button"
            onClick={advance}
            disabled={!showNext}
            onPointerDown={(e) => e.stopPropagation()}
            className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {currentStep === 0 ? t('tutorial.overlay.start') :
             currentStep >= totalSteps - 1 ? t('tutorial.overlay.finish') :
             t('tutorial.overlay.nextStep')}
          </button>
        </div>
      </div>

      {confirmExit && (
        <div
          className="absolute inset-0 bg-black/60 flex items-center justify-center"
          style={{ pointerEvents: 'auto' }}
        >
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm text-center">
            <h3 className="text-lg font-bold text-gray-900 mb-2">{t('tutorial.overlay.confirmExitTitle')}</h3>
            <p className="text-sm text-gray-600 mb-4">
              {t('tutorial.overlay.confirmExitBody')}
            </p>
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={() => setConfirmExit(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
              >
                {t('tutorial.overlay.continue')}
              </button>
              <button
                type="button"
                onClick={() => { setConfirmExit(false); exit(); }}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
              >
                {t('tutorial.overlay.confirmExit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(overlay, document.body);
}
